import type { KartState } from "./types";

/** A short, deliberately sub-item reward for a clean side-on kart contact. */
export const SIDE_BUMP_BOOST_SECONDS = 0.3;

const KART_CONTACT_DISTANCE = 3.35;
const MIN_CLOSING_SPEED = 3.5;
const MIN_FORWARD_SPEED = 12;
const MIN_HEADING_ALIGNMENT = 0.72;
const MIN_SIDE_ALIGNMENT = 0.62;

interface ContactVector {
  x: number;
  z: number;
}

function forward(kart: Pick<KartState, "yaw">): ContactVector {
  return { x: Math.sin(kart.yaw), z: Math.cos(kart.yaw) };
}

function dot(a: ContactVector, b: ContactVector): number {
  return a.x * b.x + a.z * b.z;
}

function velocity(kart: Pick<KartState, "velocityX" | "velocityZ">): ContactVector {
  return { x: kart.velocityX, z: kart.velocityZ };
}

function isEligible(kart: Pick<KartState, "finishedAt" | "airborne" | "spinTime" | "recoveryTime">): boolean {
  return kart.finishedAt === null && !kart.airborne && kart.spinTime <= 0 && kart.recoveryTime <= 0;
}

/**
 * Returns the kart that initiated a deliberate side-swipe, if this pair is a
 * valid contact.  The check is independent of array order: the normal is
 * derived from `a` to `b`, then approach speed is attributed to whichever kart
 * is actually moving toward the other.  This keeps the reward deterministic
 * for local, AI, Twitch and server-authoritative simulation alike.
 */
export function getSideBumpAttacker(a: KartState, b: KartState): KartState | null {
  if (!isEligible(a) || !isEligible(b) || a.surface !== b.surface || Math.abs(a.y - b.y) > 2.2) return null;

  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const distance = Math.hypot(dx, dz);
  if (distance < 1e-5 || distance >= KART_CONTACT_DISTANCE) return null;

  const forwardA = forward(a);
  const forwardB = forward(b);
  if (dot(forwardA, forwardB) < MIN_HEADING_ALIGNMENT) return null;

  const normal = { x: dx / distance, z: dz / distance };
  // `rightA` is the kart's lateral axis. A rear/front impact has a near-zero
  // side dot; a side-swipe has a strong one regardless of which side is used.
  const rightA = { x: forwardA.z, z: -forwardA.x };
  if (Math.abs(dot(normal, rightA)) < MIN_SIDE_ALIGNMENT) return null;

  const normalVelocityA = dot(velocity(a), normal);
  const normalVelocityB = dot(velocity(b), normal);
  const closingSpeed = normalVelocityA - normalVelocityB;
  if (closingSpeed < MIN_CLOSING_SPEED) return null;

  const speedA = dot(velocity(a), forwardA);
  const speedB = dot(velocity(b), forwardB);
  const approachA = Math.max(0, normalVelocityA);
  const approachB = Math.max(0, -normalVelocityB);
  const attacker = approachA >= approachB ? a : b;
  const attackerSpeed = attacker === a ? speedA : speedB;
  return attackerSpeed >= MIN_FORWARD_SPEED ? attacker : null;
}

/**
 * Apply the one-shot side-bump reward and return the rewarded kart. Existing
 * boost presentation, audio and network fields are intentionally reused; a
 * kart already boosting cannot refresh the reward every collision tick.
 */
export function applySideBumpBoost(a: KartState, b: KartState): KartState | null {
  const attacker = getSideBumpAttacker(a, b);
  if (!attacker || attacker.boostTime > 0) return null;
  attacker.boostTime = SIDE_BUMP_BOOST_SECONDS;
  return attacker;
}
